import { useEffect, useMemo, useState } from "react";
import "./BecasTab.css";

const LINEAS_BECA = {
  pedagogia: "Pedagogía",
  lenguaje: "Lenguaje",
};

const PRECIO_SESION_BECA = {
  pedagogia: 45,
  lenguaje: 40,
};

function euros(n) {
  return `${Number(n || 0).toFixed(2)} €`;
}

function normalizarTexto(texto) {
  return String(texto || "")
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

function contieneAlguno(texto, terminos) {
  return terminos.some((t) => texto.includes(t));
}

function detectarLineaBeca(serviceText) {
  const txt = normalizarTexto(serviceText);

  const esPedagogia = contieneAlguno(txt, [
    "reeducacion pedagogica",
    "reeducacion psicopedagogica",
    "psicopedagogia",
    "pedagogica",
    "pedagogica neae",
    "reeducacion_pedagogica",
    "reeducacion_psicopedagogica",
    "reeducacion_pedagogica_neae_mes",
    "reeducacion pedagogica neae mes",
    "altas capacidades",
    "altas_capacidades",
    "altas_capacidades_neae_mes",
    "altas capacidades neae mes",
    "intervencion_reeducacion_neae_mes",
    "intervencion reeducacion neae mes",
  ]);

  const esLenguaje = contieneAlguno(txt, [
    "reeducacion lenguaje",
    "reeducacion_lenguaje",
    "reeducacion_lenguaje_neae_mes",
    "reeducacion lenguaje neae mes",
    "lenguaje neae",
    "logopedia neae",
    "apoyo lenguaje",
    "apoyo_lenguaje",
    "logopedia",
    "deglucion",
  ]);

  if (esPedagogia) return "pedagogia";
  if (esLenguaje) return "lenguaje";

  return null;
}

export default function BecasTab({
  mes,
  setMes,
  citasRealizadas = [],
  sesionesPendientes = [],
  precios = {},
  normalizeServiceCode,
}) {
  const [becasConfig, setBecasConfig] = useState([]);
  const [loading, setLoading] = useState(false);

  const [nuevoPatientId, setNuevoPatientId] = useState("");
  const [nuevaLinea, setNuevaLinea] = useState("pedagogia");
  const [nuevaCuantia, setNuevaCuantia] = useState("");
  const [nuevoGastadoOtroCentro, setNuevoGastadoOtroCentro] = useState("");
  const [nuevasObservaciones, setNuevasObservaciones] = useState("");

  const API_URL = "/back/api/becas-config.php";

  const getPriceFromService = (serviceCode) => {
    const key = normalizeServiceCode(serviceCode);
    return Number(precios[key] || 0);
  };

  const cargarBecas = async () => {
    try {
      setLoading(true);

      const res = await fetch(API_URL, {
        credentials: "include",
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "No se pudieron cargar las becas");
      }

      const items = (data.items || []).map((b) => ({
        id: b.id,
        patientKey: `${
          b.patient_id != null
            ? `id-${b.patient_id}`
            : `name-${normalizarTexto(b.patient_name)}`
        }-${b.linea}`,
        patient_id: b.patient_id,
        nombre: b.patient_name,
        linea: b.linea,
        cuantia: Number(b.cuantia || 0),
        gastadoOtroCentro: Number(b.gastado_otro_centro || 0),
        observaciones: b.observaciones || "",
      }));

      setBecasConfig(items);
    } catch (err) {
      console.error("Error cargando becas:", err);
      alert(err.message || "Error cargando becas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarBecas();
  }, []);

  const pacientesDisponibles = useMemo(() => {
    const mapa = new Map();

    (citasRealizadas || []).forEach((c) => {
      const patientId = c.patient_id ?? c.paciente_id ?? null;
      const nombre =
        `${c.paciente || c.nombre || ""} ${c.apellidos || ""}`.trim();

      if (!nombre) return;

      const key =
        patientId != null
          ? `id-${patientId}`
          : `name-${normalizarTexto(nombre)}`;

      if (!mapa.has(key)) {
        mapa.set(key, {
          id: patientId,
          nombre,
          key,
        });
      }
    });

    (sesionesPendientes || []).forEach((s) => {
      const patientId = s.patient_id ?? s.paciente_id ?? null;
      const nombre = `${s.nombre || ""} ${s.apellidos || ""}`.trim();

      if (!nombre) return;

      const key =
        patientId != null
          ? `id-${patientId}`
          : `name-${normalizarTexto(nombre)}`;

      if (!mapa.has(key)) {
        mapa.set(key, {
          id: patientId,
          nombre,
          key,
        });
      }
    });

    return Array.from(mapa.values()).sort((a, b) =>
      a.nombre.localeCompare(b.nombre, "es"),
    );
  }, [citasRealizadas, sesionesPendientes]);

  const movimientos = useMemo(() => {
    const realizadas = (citasRealizadas || []).map((c) => {
      const nombre =
        `${c.paciente || c.nombre || ""} ${c.apellidos || ""}`.trim() ||
        "Sin nombre";

      // IMPORTANTE: priorizar service_code
      const serviceText =
        c.service_code || c.service_name || c.servicio || c.service || "";
      const linea = detectarLineaBeca(serviceText);

      return {
        tipo: "realizada",
        patient_id: c.patient_id ?? c.paciente_id ?? null,
        paciente: nombre,
        service_text: serviceText,
        service_code: c.service_code || "",
        amount: getPriceFromService(c.service_code),
        date: c.start_datetime,
        linea,
      };
    });

    const pendientes = (sesionesPendientes || []).map((s) => {
      const nombre =
        `${s.nombre || ""} ${s.apellidos || ""}`.trim() || "Sin nombre";

      // IMPORTANTE: priorizar service_code
      const serviceText = s.service_code || s.service_name || "";
      const linea = detectarLineaBeca(serviceText);

      return {
        tipo: "pendiente",
        patient_id: s.patient_id ?? s.paciente_id ?? null,
        paciente: nombre,
        service_text: serviceText,
        service_code: s.service_code || "",
        amount: getPriceFromService(s.service_code),
        date: s.start_datetime,
        linea,
      };
    });

    return [...realizadas, ...pendientes];
  }, [citasRealizadas, sesionesPendientes, precios, normalizeServiceCode]);

  const resumenBecas = useMemo(() => {
    return becasConfig.map((b) => {
      const movimientosPaciente = movimientos.filter((m) => {
        if (b.patient_id != null && m.patient_id != null) {
          return Number(m.patient_id) === Number(b.patient_id);
        }

        return normalizarTexto(m.paciente) === normalizarTexto(b.nombre);
      });

      const sesionesRealizadasLinea = movimientosPaciente.filter(
        (m) => m.tipo === "realizada" && m.linea === b.linea,
      );

      const precioSesion = PRECIO_SESION_BECA[b.linea] || 0;
      const sesionesComputables = sesionesRealizadasLinea.length;
      const totalConsumidoAqui = sesionesComputables * precioSesion;

      const cuantia = Number(b.cuantia || 0);
      const gastadoOtroCentro = Number(b.gastadoOtroCentro || 0);

      const saldoRestante = Math.max(
        cuantia - gastadoOtroCentro - totalConsumidoAqui,
        0,
      );

      const sesionesRestantes =
        precioSesion > 0 ? Math.floor(saldoRestante / precioSesion) : 0;

      return {
        ...b,
        sesionesComputables,
        totalConsumidoAqui,
        saldoRestante,
        precioSesion,
        sesionesRestantes,
      };
    });
  }, [becasConfig, movimientos]);

  const handleAddBeca = async () => {
    const pacienteSeleccionado = pacientesDisponibles.find(
      (p) => p.key === nuevoPatientId,
    );

    if (!pacienteSeleccionado) {
      alert("Selecciona un paciente");
      return;
    }

    try {
      const payload = {
        patient_id: pacienteSeleccionado.id,
        patient_name: pacienteSeleccionado.nombre,
        linea: nuevaLinea,
        cuantia: Number(nuevaCuantia || 0),
        gastado_otro_centro: Number(nuevoGastadoOtroCentro || 0),
        observaciones: nuevasObservaciones,
      };

      const res = await fetch(API_URL, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "No se pudo crear la beca");
      }

      await cargarBecas();

      setNuevoPatientId("");
      setNuevaLinea("pedagogia");
      setNuevaCuantia("");
      setNuevoGastadoOtroCentro("");
      setNuevasObservaciones("");
    } catch (err) {
      console.error("Error creando beca:", err);
      alert(err.message || "Error creando beca");
    }
  };

  const handleDelete = async (b) => {
    if (!b.id) {
      alert("Esta beca no tiene id y no se puede borrar");
      return;
    }

    try {
      const res = await fetch(`${API_URL}?id=${b.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "No se pudo eliminar la beca");
      }

      await cargarBecas();
    } catch (err) {
      console.error("Error eliminando beca:", err);
      alert(err.message || "Error eliminando beca");
    }
  };

  const handleUpdate = async (b, field, value) => {
    if (!b.id) {
      alert("Esta beca no tiene id y no se puede actualizar");
      return;
    }

    const actualizado = {
      patient_id: b.patient_id,
      patient_name: b.nombre,
      linea: b.linea,
      cuantia:
        field === "cuantia" ? Number(value || 0) : Number(b.cuantia || 0),
      gastado_otro_centro:
        field === "gastadoOtroCentro"
          ? Number(value || 0)
          : Number(b.gastadoOtroCentro || 0),
      observaciones:
        field === "observaciones" ? value : (b.observaciones || ""),
    };

    try {
      const res = await fetch(`${API_URL}?id=${b.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(actualizado),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || "No se pudo actualizar la beca");
      }

      await cargarBecas();
    } catch (err) {
      console.error("Error actualizando beca:", err);
      alert(err.message || "Error actualizando beca");
    }
  };

  const totalResumen = useMemo(() => {
    return resumenBecas.reduce(
      (acc, item) => {
        acc.cuantia += Number(item.cuantia || 0);
        acc.otroCentro += Number(item.gastadoOtroCentro || 0);
        acc.consumidoAqui += Number(item.totalConsumidoAqui || 0);
        acc.saldo += Number(item.saldoRestante || 0);
        return acc;
      },
      {
        cuantia: 0,
        otroCentro: 0,
        consumidoAqui: 0,
        saldo: 0,
      },
    );
  }, [resumenBecas]);

  return (
    <div className="becas-tab">
      <div className="becas-header">
        <div>
          <h3>Control de becas</h3>
          <p className="becas-subtitle">
            Se descuentan solo las sesiones ya dadas. Lenguaje: 40 € por sesión.
            Pedagogía: 50 € por sesión.
          </p>
        </div>

        <label className="becas-month-label">
          Mes:
          <input
            type="month"
            value={mes}
            onChange={(e) => setMes(e.target.value)}
          />
        </label>
      </div>

      {loading && <p>Cargando becas...</p>}

      <div className="becas-form-card">
        <h4>Añadir beca</h4>

        <div className="becas-form">
          <select
            value={nuevoPatientId}
            onChange={(e) => setNuevoPatientId(e.target.value)}
          >
            <option value="">Selecciona paciente</option>
            {pacientesDisponibles.map((p) => (
              <option key={p.key} value={p.key}>
                {p.nombre}
              </option>
            ))}
          </select>

          <select
            value={nuevaLinea}
            onChange={(e) => setNuevaLinea(e.target.value)}
          >
            <option value="pedagogia">Pedagogía</option>
            <option value="lenguaje">Lenguaje</option>
          </select>

          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Cuantía concedida"
            value={nuevaCuantia}
            onChange={(e) => setNuevaCuantia(e.target.value)}
          />

          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Gastado en otro centro"
            value={nuevoGastadoOtroCentro}
            onChange={(e) => setNuevoGastadoOtroCentro(e.target.value)}
          />

          <textarea
            rows={2}
            placeholder="Observaciones (opcional)"
            value={nuevasObservaciones}
            onChange={(e) => setNuevasObservaciones(e.target.value)}
          />

          <button onClick={handleAddBeca}>➕ Añadir</button>
        </div>
      </div>

      <div className="becas-summary-cards">
        <div className="becas-summary-card">
          <span className="label">Cuantía total</span>
          <strong>{euros(totalResumen.cuantia)}</strong>
        </div>
        <div className="becas-summary-card">
          <span className="label">Otro centro</span>
          <strong>{euros(totalResumen.otroCentro)}</strong>
        </div>
        <div className="becas-summary-card">
          <span className="label">Consumido aquí</span>
          <strong>{euros(totalResumen.consumidoAqui)}</strong>
        </div>
        <div className="becas-summary-card">
          <span className="label">Saldo restante</span>
          <strong>{euros(totalResumen.saldo)}</strong>
        </div>
      </div>

      <div className="becas-table-wrap">
        <table className="becas-table">
          <thead>
            <tr>
              <th>Paciente</th>
              <th>Línea</th>
              <th>Cuantía</th>
              <th>Otro centro</th>
              <th>Consumido aquí</th>
              <th>Saldo restante</th>
              <th>Sesiones dadas</th>
              <th>Precio sesión</th>
              <th>Sesiones restantes</th>
              <th>Observaciones</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {resumenBecas.length === 0 ? (
              <tr>
                <td colSpan="11">No hay becas configuradas.</td>
              </tr>
            ) : (
              resumenBecas.map((b) => (
                <tr key={b.patientKey}>
                  <td>{b.nombre}</td>
                  <td>{LINEAS_BECA[b.linea] || b.linea}</td>

                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={b.cuantia}
                      onChange={(e) =>
                        handleUpdate(b, "cuantia", e.target.value)
                      }
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={b.gastadoOtroCentro}
                      onChange={(e) =>
                        handleUpdate(b, "gastadoOtroCentro", e.target.value)
                      }
                    />
                  </td>

                  <td>{euros(b.totalConsumidoAqui)}</td>
                  <td>{euros(b.saldoRestante)}</td>
                  <td>{b.sesionesComputables}</td>
                  <td>{euros(b.precioSesion)}</td>
                  <td>{b.sesionesRestantes}</td>

                  <td>
                    <textarea
                      rows={2}
                      value={b.observaciones || ""}
                      onChange={(e) =>
                        handleUpdate(b, "observaciones", e.target.value)
                      }
                    />
                  </td>

                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(b)}
                    >
                      🗑 Eliminar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="becas-nota">
        <p>
          El sistema detecta automáticamente las sesiones de beca según la
          descripción o código del servicio.
        </p>
        <p>
          Para el cálculo se descuentan solo las sesiones ya dadas:
          lenguaje a 40 € y pedagogía a 45 €.
        </p>
      </div>
    </div>
  );
}